import { ImagesType } from "@features/images/type";
import { useRelatedImages } from "@features/images/use-related-images";
import { Separator, Spinner } from "@radix-ui/themes";
import _ from "lodash";
import React from "react";
import { twMerge } from "tailwind-merge";
import { RelationshipImageThumbnail } from "./relationship-image-thumbnail";

interface ImageRelationshipNavigatorProps {
  currentImageId: string;
  className?: string;
}

const ImageStack: React.FC<{
  images: ImagesType[];
  parentId: string;
  activeImageId: string;
  active?: boolean;
}> = ({ images, parentId, activeImageId, active }) => {
  const activeImage = images.find((img) => img.id === activeImageId);
  const otherImages = images.filter((img) => img.id !== activeImageId);
  const hasMultiple = images.length > 1;

  if (!activeImage) return <></>;

  return (
    <div className="relative">
      <>
        <RelationshipImageThumbnail
          className="z-10 border border-white dark:border-slate-950"
          key={activeImage!.id}
          image={activeImage}
          withBullet
          isActive={active}
        />
        {hasMultiple && (
          <div className="z-20 absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center z-20 font-bold">
            {images.length}
          </div>
        )}
        {otherImages.slice(0, 2).map((img, index) => (
          <div
            key={img.id}
            className={twMerge(
              "absolute w-10 h-10 bg-gray-200 dark:bg-gray-600 border border-white dark:border-slate-950 rounded top-0 left-0 pointer-events-none transition-all duration-200"
            )}
            style={{
              zIndex: 5 - index,
              transform: `translate(${(index + 1) * 4}px, -${
                (index + 1) * 4
              }px)`,
              opacity: 0.6 - index * 0.3,
              backgroundImage: `url(${img.preview_url || img.url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        ))}
      </>
    </div>
  );
};

export const ImageRelationshipNavigator: React.FC<
  ImageRelationshipNavigatorProps
> = ({ currentImageId, className = "" }) => {
  const {
    data: relatedData,
    isLoading,
    error,
  } = useRelatedImages(currentImageId);

  if (isLoading) {
    return (
      <div
        className={twMerge(
          "flex items-center justify-center p-2 w-10 h-10 bg-slate-500 rounded",
          className
        )}
      >
        <Spinner size="1" />
      </div>
    );
  }

  if (error || !relatedData) {
    return <></>;
  }

  const { pathToTarget, siblingGroups, immediateChildren } = relatedData;

  if (pathToTarget.length === 0 && immediateChildren.length === 0) {
    return (
      <div
        className={twMerge(
          "flex items-center justify-center p-2 text-sm text-gray-500",
          className
        )}
      >
        No related images
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        "flex items-center space-x-1 pb-2 pt-4 -mb-3 overflow-x-auto",
        className
      )}
    >
      {["root", ...pathToTarget].slice(0, -2).map((id, i) => (
        <ImageStack
          key={id}
          images={siblingGroups[id] || []}
          parentId={id}
          activeImageId={
            siblingGroups[id]?.find((img) => pathToTarget.includes(img.id))
              ?.id || ""
          }
        />
      ))}
      {pathToTarget.length > 1 && <Separator />}
      {_.sortBy(
        siblingGroups[pathToTarget[pathToTarget.length - 2] || "root"] || [],
        (a) => a.created_at
      ).map((image, i) => (
        <ImageStack
          key={image.id + "-" + i}
          images={[image]}
          parentId={image.from_image_id || "root"}
          activeImageId={image.id}
          active={image.id === currentImageId}
        />
      ))}
      {immediateChildren.length > 0 && <Separator />}
      {_.sortBy(immediateChildren, (a) => a.created_at).map((image, i) => (
        <ImageStack
          key={image.id + "-child-" + i}
          images={[image]}
          parentId={currentImageId}
          activeImageId={image.id}
        />
      ))}
    </div>
  );
};
